// #genai Custom hook that polls pollEmailVerification (email-verifications/{verificationId}).
// Waits INITIAL_DELAY_MS before the first poll, then retries every POLL_INTERVAL_MS.
// Stops after MAX_POLL_DURATION_MS with a timeout error.
// After a verified token is received, stores the token and redirects.
// The checkResourceAccess (hasAccess) check is deferred to SharedStartupGate
// which runs after the full-page redirect.
import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthToken } from 'utils/app.utils';
import {
  pollEmailVerificationService,
  buildTokenPayload,
} from 'services/GuestAccess.service';
import { setIsShareToken } from 'features/authorize/Authorize.actions';
import { SHARED_LOGIN_ID_KEY } from '../../constants/AppConstants';
import {
  setGuestVerified,
  setGuestFlowPhase,
  setGuestError,
  setGuestUserInfo,
} from './GuestAccess.actions';
import {
  ACCESS_PHASES,
  POLLING_CONFIG,
  VERIFICATION_STATUS,
  GUEST_ERROR_MESSAGES,
  GUEST_ROUTES,
} from '../../constants/GuestAccess';

/**
 * Starts polling pollEmailVerification when `isActive` is true.
 * First poll fires after INITIAL_DELAY_MS; subsequent polls every POLL_INTERVAL_MS.
 *
 * @param {object} options
 * @param {string}  options.verificationId - ID returned by sendEmailVerification
 * @param {boolean} options.isActive       - Enable / disable the polling loop
 * @param {string}  options.redirectTo     - Path to redirect to after token is stored
 */
const useGuestVerificationPolling = ({ verificationId, isActive, redirectTo = GUEST_ROUTES.DEFAULT_REDIRECT }) => {
  const dispatch = useDispatch();
  const { loginId } = useSelector((state) => state.GuestAccess);
  const initialDelayRef = useRef(null);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const isActiveRef = useRef(isActive);
  const loginIdRef = useRef(loginId);

  useEffect(() => { loginIdRef.current = loginId; }, [loginId]);

  // Keep ref in sync so the interval callback sees the latest value.
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  const stopPolling = useCallback(() => {
    if (initialDelayRef.current) {
      clearTimeout(initialDelayRef.current);
      initialDelayRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isActive || !verificationId) return undefined;

    startTimeRef.current = Date.now();

    const poll = async () => {
      // Stop if deactivated between ticks.
      if (!isActiveRef.current) {
        stopPolling();
        return;
      }

      // Max-poll-duration exceeded.
      if (Date.now() - startTimeRef.current >= POLLING_CONFIG.MAX_POLL_DURATION_MS) {
        stopPolling();
        dispatch(setGuestFlowPhase(ACCESS_PHASES.ERROR));
        dispatch(setGuestError(GUEST_ERROR_MESSAGES.TIMEOUT));
        return;
      }

      try {
        const { ok, json = {} } = await pollEmailVerificationService(verificationId);

        if (!ok) return; // transient error — keep polling

        const {
          status,
          customAccessToken: tokenData,
          errorDescription = '',
        } = json || {};
        // API returns "VERIFIED" (uppercase); also accept lowercase for resilience.
        const isVerified = status === VERIFICATION_STATUS.VERIFIED || status === VERIFICATION_STATUS.VERIFIED_LC;
        const isFailed = status === VERIFICATION_STATUS.FAILED || status === VERIFICATION_STATUS.FAILED_LC;
        if (isFailed) {
          stopPolling();
          dispatch(setGuestError(errorDescription || GUEST_ERROR_MESSAGES.VERIFICATION_FAILED));
          dispatch(setGuestFlowPhase(ACCESS_PHASES.ERROR));
          return;
        }
        if (isVerified && tokenData?.access_token) {
          stopPolling();
          const tokenPayload = buildTokenPayload(tokenData);
          setAuthToken(tokenPayload);
          dispatch(setIsShareToken(true));
          dispatch(setGuestVerified({ isVerified: true, customAuthToken: tokenData.access_token }));
          // Guest magic-link users are never EV employees. #genai
          dispatch(setGuestUserInfo({ isEvUser: false }));

          // Persist loginId so SharedStartupGate fires the audit after redirect.
          const currentLoginId = loginIdRef.current;
          if (currentLoginId) {
            try { sessionStorage.setItem(SHARED_LOGIN_ID_KEY, currentLoginId); } catch (_) { /* noop */ }
          }

          dispatch(setGuestFlowPhase(ACCESS_PHASES.VERIFIED));
          window.location.assign(redirectTo);
        }
      } catch (_) {
        // Network error — keep polling silently.
      }
    };

    // Initial delay then periodic polling.
    initialDelayRef.current = setTimeout(() => {
      poll();
      intervalRef.current = setInterval(poll, POLLING_CONFIG.POLL_INTERVAL_MS);
    }, POLLING_CONFIG.INITIAL_DELAY_MS);

    return () => stopPolling();
  }, [isActive, verificationId, redirectTo, dispatch, stopPolling]);
};

export default useGuestVerificationPolling;
